/**
 * The menu a clicked label opens, for rating, tagging and describing it. Whatever changes the label's look on the
 * canvas (opening, closing, a severity pick) is announced through onChange, since the canvas is built after this.
 */

import i18next from 'i18next';
import { ShareWidget } from '../../common/share/ShareWidget.js';
import { util } from '../../common/utilities.js';
import '../../common/utilitiesSidewalk.js';
import '../../../css/components/tag-pills.css';
/** @typedef {import('../Main.js').ExploreConfig} ExploreConfig */
/** @typedef {import('../Main.js').ContextMenuUi} ContextMenuUi */
/** @typedef {import('./CanvasFrame.js').CanvasFrame} CanvasFrame */
/** @typedef {import('../data/Form.js').Form} Form */
/** @typedef {import('../label/Label.js').Label} Label */
/** @typedef {import('../label/LabelContainer.js').LabelContainer} LabelContainer */
/** @typedef {import('../alert/RatingReminderAlert.js').RatingReminderAlert} RatingReminderAlert */
/** @typedef {import('../menu/LabelModes.js').LabelModes} LabelModes */
/** @typedef {import('../navigation/NavigationService.js').NavigationService} NavigationService */
/** @typedef {import('../data/Tracker.js').Tracker} Tracker */

export class ContextMenu {
  labelTags; // Public: read by Keyboard.js. Populated by fetchLabelTags().

  #config;
  #frame;
  #labelContainer;
  #labelModes;
  #ratingReminderAlert;
  #form;
  #tracker;

  /** @type {Array<() => void>} */
  #changeListeners = [];

  /** @type {Array<(focused: boolean) => void>} */
  #textFieldFocusListeners = [];

  #status = {
    targetLabel: null,
    visibility: 'hidden',
    ratingSeverityEnabledForTutorialLabel: undefined, // During tutorial, disabled except for specific steps
    taggingEnabledForTutorialLabel: undefined, // During tutorial, disabled except for specific steps
  };

  #menuWindow;
  #severityMenu;
  #severityRadioHolder;
  #severityRadios;
  #descriptionTextBox;
  #headerIcon;
  #headerType;
  #tagHolder;
  #tags = [];
  #shareWidget = null;

  /**
   * @param {ContextMenuUi} uiContextMenu - The context menu's DOM elements.
   * @param {ExploreConfig} config
   * @param {CanvasFrame} frame - Says how big the label icon the menu is anchored to is.
   * @param {LabelContainer} labelContainer - Owns the labels this menu edits and whether they may be deleted.
   * @param {LabelModes} labelModes - Kept from switching modes while the description is being typed.
   * @param {NavigationService} navigationService - Whose moves close the menu.
   * @param {RatingReminderAlert} ratingReminderAlert - Told whether the label was rated when the menu closes.
   * @param {Form} form - Saves the label before it is shared.
   * @param {Tracker} tracker
   */
  constructor(uiContextMenu, config, frame, labelContainer, labelModes, navigationService, ratingReminderAlert, form,
    tracker) {
    this.#config = config;
    this.#frame = frame;
    this.#labelContainer = labelContainer;
    this.#labelModes = labelModes;
    this.#ratingReminderAlert = ratingReminderAlert;
    this.#form = form;
    this.#tracker = tracker;
    this.#menuWindow = uiContextMenu.holder;
    this.#severityMenu = uiContextMenu.severityMenu;
    this.#severityRadioHolder = uiContextMenu.severityRadioHolder;
    this.#severityRadios = uiContextMenu.radioButtons;
    this.#descriptionTextBox = uiContextMenu.textBox;
    this.#headerIcon = this.#menuWindow.querySelector('#context-menu-icon');
    this.#headerType = this.#menuWindow.querySelector('#context-menu-type');
    this.#tagHolder = uiContextMenu.tagHolder;
    this.#initShareWidget();

    navigationService.onMoveStarted(() => {
      if (this.isOpen()) this.hide();
    });
    document.addEventListener('mousedown', (e) => this.#handleMouseDown(e));
    this.#menuWindow.addEventListener('mousedown', (e) => this.#handleMenuWindowMouseDown(e));
    for (const radio of this.#severityRadios) radio.addEventListener('change', (e) => this.#handleSeverityChange(e));
    this.#descriptionTextBox.addEventListener('change', (e) => this.#handleDescriptionTextBoxChange(e));
    this.#descriptionTextBox.addEventListener('focus', () => this.#handleDescriptionTextBoxFocus());
    this.#descriptionTextBox.addEventListener('blur', () => this.#handleDescriptionTextBoxBlur());
    uiContextMenu.closeButton.addEventListener('click', () => this.#handleCloseButtonClick());
    this.#menuWindow.querySelector('#context-menu-done')
      ?.addEventListener('click', () => this.#handleDoneButtonClick());
    this.#menuWindow.querySelector('#context-menu-delete')
      ?.addEventListener('click', () => this.#handleDeleteButtonClick());
  }

  /**
   * Told when the menu opened or closed or a severity was picked, each of which changes how the label is drawn.
   * @param {() => void} listener
   */
  onChange(listener) {
    this.#changeListeners.push(listener);
  }

  /**
   * Told when the description box takes or loses focus, so keyboard shortcuts stay off while typing.
   * @param {(focused: boolean) => void} listener
   */
  onTextFieldFocusChanged(listener) {
    this.#textFieldFocusListeners.push(listener);
  }

  /**
   * Selects a severity the way a click on its segment would, `change` event included.
   * @param {number} value
   */
  checkRadioButton(value) {
    const radio = this.#severityRadios.find((r) => parseInt(r.value, 10) === value);
    if (!radio) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #getStatus(key) {
    return this.#status[key];
  }

  #setStatus(key, value) {
    this.#status[key] = value;
  }

  getTargetLabel() {
    return this.#status.targetLabel;
  }

  /**
   * Combined with the document mousedown listener, closes the context menu window when the user clicks somewhere
   * outside the context menu window.
   * @param {Event} e
   */
  #handleMenuWindowMouseDown(e) {
    e.stopPropagation();
  }

  /**
   * Handles clicking outside of context menu holder. If so, close the context menu and note if user rated severity.
   * @param {Event} e
   */
  #handleMouseDown(e) {
    const clickedOut = !this.#menuWindow.contains(/** @type {Node} */ (e.target));
    if (this.isOpen()) {
      if (clickedOut) {
        this.#tracker.push('ContextMenu_CloseClickOut', null, this.#status.targetLabel);
        this.handleSeverityPopup();
      }
      this.hide();
    }
  }

  #handleDescriptionTextBoxChange(e) {
    const description = /** @type {HTMLInputElement} */ (e.currentTarget).value;
    this.#tracker.push('ContextMenu_TextBoxChange', { Description: description }, this.#status.targetLabel);
    if (this.#status.targetLabel) {
      this.#status.targetLabel.edit({ description });
    }
  }

  #handleDescriptionTextBoxBlur() {
    this.#tracker.push('ContextMenu_TextBoxBlur', null, this.#status.targetLabel);
    this.#labelModes.switching.release('description');
    this.#textFieldFocusListeners.forEach((listener) => listener(false));
  }

  #handleDescriptionTextBoxFocus() {
    this.#tracker.push('ContextMenu_TextBoxFocus', null, this.#status.targetLabel);
    this.#labelModes.switching.hold('description');
    this.#textFieldFocusListeners.forEach((listener) => listener(true));
  }

  #handleCloseButtonClick() {
    this.#tracker.push('ContextMenu_CloseButtonClick', null, this.#status.targetLabel);
    this.handleSeverityPopup();
    this.hide();
  }

  #handleDoneButtonClick() {
    this.#tracker.push('ContextMenu_DoneButtonClick', null, this.#status.targetLabel);
    this.handleSeverityPopup();
    this.hide();
  }

  /**
   * Deletes the label being edited. The panel opens automatically when a label is placed, so this is the way out
   * of a misplaced one without closing, re-finding and hovering it first. Mirrors the collapsed card's Delete
   * (Canvas.js #handleHoverCardDeleteClick), including the tutorial's last-label-only restriction.
   */
  #handleDeleteButtonClick() {
    const label = this.#status.targetLabel;
    if (!this.#labelContainer.canDelete(label)) return;

    this.#tracker.push('ContextMenu_LabelDelete', { labelType: label.getLabelType() }, label);
    this.hide();
    this.#labelContainer.removeLabel(label);
  }

  handleSeverityPopup() {
    const labels = this.#labelContainer.getAllLabels();
    if (labels.length > 0) {
      const lastLabelProps = labels[labels.length - 1].getProperties();
      // Only call ratingReminderAlert() for label types that have a severity rating.
      if (util.misc.labelTypeHasSeverity(lastLabelProps.labelType)) {
        this.#ratingReminderAlert.ratingClicked(lastLabelProps.severity);
      }
    }
  }

  #handleSeverityChange(e) {
    const severity = parseInt(/** @type {HTMLInputElement} */ (e.currentTarget).value, 10);
    const label = this.#status.targetLabel;
    this.#tracker.push('ContextMenu_RadioChange', { LabelType: label.getLabelType(), RadioValue: severity }, label);

    this.updateRadioButtonImages();
    label.edit({ severity });
    this.#changeListeners.forEach((listener) => listener());
  }

  /**
   * Fetches the label tags from the server, stores them, then invokes the callback.
   * @param {Function} callback
   */
  fetchLabelTags(callback) {
    fetch('/label/tags', { method: 'GET', headers: { 'Content-Type': 'application/json; charset=utf-8' } })
      .then((res) => {
        if (!res.ok) throw res;
        return res.json();
      })
      .then((json) => {
        this.labelTags = json;
        callback();
      });
  }

  /**
   * Update the severity smiley icons to reflect the current label type (positive vs negative icon set) and which
   * severity level is currently selected (filled vs outline variant).
   */
  updateRadioButtonImages() {
    if (!this.#severityRadioHolder) return;
    const checkedSev = Number(this.#severityRadios.find((r) => r.checked)?.value);
    const labelType = this.#status.targetLabel.getLabelType();
    this.#severityRadioHolder.querySelectorAll('.severity-button').forEach((button) => {
      const sev = Number(button.dataset.severity);
      const img = button.querySelector('.severity-button__icon');
      if (img) img.src = util.misc.getSmileyIconPath(sev, labelType, sev === checkedSev);
      // The chosen segment is washed and underlined in its own level's colour. The selected state is marked with
      // a class rather than left to CSS to infer from :has(:checked) -- the radio is visually hidden and driven
      // from code, and this keeps the styling keyed to the same value that picks the smiley above.
      button.classList.toggle('severity-button--checked', sev === checkedSev);
      const colors = util.misc.getSeverityLevelColors(sev, labelType);
      if (colors) {
        button.style.setProperty('--level-wash', colors.wash);
        button.style.setProperty('--level-edge', colors.edge);
      }
    });
  }

  /**
   * Swap the context menu's rating section text based on whether the current label type is a positive type.
   */
  #updateRatingText() {
    const labelType = this.#status.targetLabel.getLabelType();
    const positive = util.misc.isPositiveLabelType(labelType);
    const headerKey = positive ? 'rate-quality' : 'rate-severity';
    const infoKey = positive ? 'rate-quality-info' : 'rate-severity-info';
    const levelKeys = util.misc.getRatingLevelKeys(labelType);

    const header = document.getElementById('severity-header-text');
    if (header) header.textContent = i18next.t(`common:${headerKey}`);
    const info = document.getElementById('severity-header-info');
    // The alt rides along because it is this icon's accessible name, and the markup's static one says "severity"
    // whichever dimension is on screen.
    if (info) {
      const infoText = i18next.t(`common:${infoKey}`);
      info.setAttribute('data-ps-tooltip', infoText);
      info.setAttribute('alt', infoText);
    }
    for (let sev = 1; sev <= 3; sev++) {
      const labels = document.querySelectorAll(`.severity-button[data-severity="${sev}"] .severity-button__label`);
      for (const label of labels) label.textContent = i18next.t(`common:${levelKeys[sev]}`);
    }
  }

  /**
   * The tag id a button stands for.
   * @param {HTMLElement} button
   * @returns {number}
   */
  static #tagIdOf(button) {
    return parseInt(button.dataset.tagId, 10);
  }

  /**
   * Records tag ID when clicked and updates tag color.
   * @param {MouseEvent} e
   */
  #handleTagClick(e) {
    const button = /** @type {HTMLButtonElement} */ (e.currentTarget);
    const label = this.#status.targetLabel;
    let labelTags = [...label.getProperty('tagIds')]; // A copy: the label's own list changes only through edit().

    // A shortcut key's click and Enter on a focused button have no cursor position, so both count as keyboard.
    const wasClickedByMouse = e.isTrusted && e.clientX !== 0 && e.clientY !== 0;

    const tag = this.labelTags.find((t) => t.tag_id === ContextMenu.#tagIdOf(button));

    // Adds or removes tag from the label's current list of tags.
    if (!labelTags.includes(tag.tag_id)) {
      // If the tag is mutually exclusive with another tag, automatically remove the other tag.
      if (tag.mutually_exclusive_with) {
        const mutuallyExclusiveTag = this.labelTags.find((t) => t.tag === tag.mutually_exclusive_with);
        if (mutuallyExclusiveTag) {
          labelTags = this.#autoRemoveAlternateTagAndUpdateUI(mutuallyExclusiveTag.tag_id, labelTags);
        }
      }

      // Log the tag click.
      labelTags.push(tag.tag_id);
      if (wasClickedByMouse) {
        this.#tracker.push('ContextMenu_TagAdded', { tagId: tag.tag_id, tagName: tag.tag }, label);
      } else {
        this.#tracker.push('KeyboardShortcut_TagAdded', { tagId: tag.tag_id, tagName: tag.tag }, label);
      }
    } else {
      labelTags.splice(labelTags.indexOf(tag.tag_id), 1);
      if (wasClickedByMouse) {
        this.#tracker.push('ContextMenu_TagRemoved', { tagId: tag.tag_id, tagName: tag.tag }, label);
      } else {
        this.#tracker.push('KeyboardShortcut_TagRemoved', { tagId: tag.tag_id, tagName: tag.tag }, label);
      }
    }
    button.classList.toggle('tag-pill--active');
    label.edit({ tagIds: labelTags });
    button.blur();
    // For events that depend on up-to-date tagIds.
    this.#tagHolder.dispatchEvent(new CustomEvent('tagIds-updated'));
  }

  /**
   * Remove the alternate tag, update UI, and add the selected tag.
   * @param {number} tagId - The id of the tag to be removed.
   * @param {number[]} labelTags - List of tags that the current label has.
   * @returns {number[]} The updated labelTags list.
   */
  #autoRemoveAlternateTagAndUpdateUI(tagId, labelTags) {
    for (const tag of this.#tags) {
      if (ContextMenu.#tagIdOf(tag) === tagId) tag.classList.remove('tag-pill--active');
    }

    // Remove tag from list of tags and log the automated removal.
    this.labelTags.forEach((tag) => {
      if (tag.tag_id === tagId && labelTags.includes(tagId)) {
        labelTags.splice(labelTags.indexOf(tag.tag_id), 1);
        this.#tracker.push('ContextMenu_TagAutoRemoved', { tagId: tag.tag_id, tagName: tag.tag },
          this.#status.targetLabel);
      }
    });
    return labelTags;
  }

  /**
   * Hide the context menu.
   * @returns {ContextMenu} this.
   */
  hide() {
    const wasOpen = this.isOpen();
    if (wasOpen) {
      // Every close logs a blur row before the close row, focused or not.
      if (document.activeElement === this.#descriptionTextBox) this.#descriptionTextBox.blur();
      else this.#handleDescriptionTextBoxBlur();
      this.#tracker.push('ContextMenu_Close', null, this.#status.targetLabel);
    }

    // The share popover lives in this panel's header, so it goes too. Hiding the panel around it would leave the
    // widget believing it is still open, with its document-level ESC and arrow-key handlers still armed.
    this.#shareWidget?.close();

    this.#menuWindow.style.visibility = 'hidden';
    this.#setStatus('visibility', 'hidden');

    // The target label's icon returns to full opacity (see Label.render). Only when the panel was actually open —
    // hide() is also called speculatively (navigation, keyboard shortcuts) and shouldn't redraw the canvas then.
    if (wasOpen) this.#changeListeners.forEach((listener) => listener());

    return this;
  }

  /**
   * Checks if the menu is open or not.
   * @returns {boolean}
   */
  isOpen() {
    return this.#getStatus('visibility') === 'visible';
  }

  // Disable rating severity.
  disableRatingSeverity() {
    this.#setStatus('ratingSeverityEnabledForTutorialLabel', null);
    this.#showRatingSeverityDisabled();
  }

  // Disable tagging.
  disableTagging() {
    this.#setStatus('taggingEnabledForTutorialLabel', null);
    this.#showTaggingDisabled();
  }

  // Enable rating severity for a given tutorial label.
  enableRatingSeverityForTutorialLabel(tutorialLabelNumber) {
    this.#setStatus('ratingSeverityEnabledForTutorialLabel', tutorialLabelNumber);
    if (this.#config.isOnboarding && !this.isRatingSeverityDisabled()) this.#showRatingSeverityEnabled();
  }

  // Enable tagging for a given tutorial label.
  enableTaggingForTutorialLabel(tutorialLabelNumber) {
    this.#setStatus('taggingEnabledForTutorialLabel', tutorialLabelNumber);
    if (this.#config.isOnboarding && !this.isTaggingDisabled()) this.#showTaggingEnabled();
  }

  // Switches the severity buttons on the current context menu back on.
  #showRatingSeverityEnabled() {
    this.#severityRadioHolder.classList.remove('is-disabled');
    for (const radio of this.#severityRadios) radio.disabled = false;
  }

  // Switches off and dims the severity buttons on the current context menu.
  #showRatingSeverityDisabled() {
    this.#severityRadioHolder.classList.add('is-disabled');
    for (const radio of this.#severityRadios) radio.disabled = true;
  }

  #showTaggingEnabled() {
    for (const tag of this.#tags) tag.disabled = false;
  }

  #showTaggingDisabled() {
    for (const tag of this.#tags) tag.disabled = true;
  }

  /**
   * Returns true if the open label can't be rated: its type has no rating, or the tutorial hasn't reached its rating.
   * @returns {boolean}
   */
  isRatingSeverityDisabled() {
    const label = this.#status.targetLabel;
    if (!label || !util.misc.labelTypeHasSeverity(label.getLabelType())) return true;
    return this.#status.ratingSeverityEnabledForTutorialLabel !== label.getProperty('tutorialLabelNumber');
  }

  /**
   * Returns true if tagging is currently disabled.
   * @returns {boolean}
   */
  isTaggingDisabled() {
    return this.#status.taggingEnabledForTutorialLabel !== this.#status.targetLabel.getProperty('tutorialLabelNumber');
  }

  /**
   * Sets the color of a label's tags based off of tags that were chosen.
   * @param {Label} label - Current label being modified.
   */
  #setTagColor(label) {
    const labelTags = label.getProperty('tagIds');
    for (const tag of this.#tags) {
      tag.classList.toggle('tag-pill--active', labelTags.includes(ContextMenu.#tagIdOf(tag)));
    }
  }

  /**
   * Builds one tag button per tag of the label's type.
   * @param {Label} label - Current label being modified.
   */
  #setTags(label) {
    this.#tags = [];
    this.#tagHolder.replaceChildren();
    const labelTags = this.labelTags;
    if (labelTags) {
      labelTags.forEach((tag) => {
        if (tag.label_type === label.getProperty('labelType')) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'context-menu-tag tag-pill tag-pill--interactive';
          button.dataset.tagId = String(tag.tag_id);

          // Set tag texts to new underlined version as defined in the util label description map.
          const tagText = util.misc.getLabelDescriptions(tag.label_type).tagInfo[tag.tag].text;
          button.innerHTML = `<span class="tag-pill__label">${tagText}</span>`;
          button.addEventListener('click', (e) => this.#handleTagClick(e));
          this.#tagHolder.append(button);
          this.#tags.push(button);

          // Add tooltip with tag example if we have an example image to show.
          // If there's a server-specific image, try that first. Get default image as a backup.
          let exampleImage;
          const imageUrl = util.assetPath(`images/examples/tags/${tag.tag_id}.png`);
          let citySpecificImageUrl;
          if (window.cityId === 'chandigarh-india') {
            citySpecificImageUrl = util.assetPath(`images/examples/tags/india/${tag.tag_id}.png`);
          } else if (['zurich', 'zurich-infra3d', 'staging'].includes(window.cityId)) {
            citySpecificImageUrl = util.assetPath(`images/examples/tags/zurich/${tag.tag_id}.png`);
          }

          if (citySpecificImageUrl) {
            exampleImage = util.getImage(citySpecificImageUrl).catch(() => util.getImage(imageUrl));
          } else {
            exampleImage = util.getImage(imageUrl);
          }

          exampleImage.then((img) => {
            const underlineClassOffset = 15;
            let keyChar;
            let tooltipHeader;
            // If first letter is used for shortcut, the string will start with "<tag-underline".
            if (tagText[0] === '<') {
              keyChar = tagText[underlineClassOffset];
              const first = tagText[underlineClassOffset].toUpperCase();
              // eslint-disable-next-line ps/escape-in-markup -- our tag text, with its underline markup.
              tooltipHeader = `${tagText.substring(0, underlineClassOffset)}${first}${tagText.substring(16)}`;
            } else {
              const underlineIndex = tagText.indexOf('<');
              keyChar = tagText[underlineIndex + underlineClassOffset];
              tooltipHeader = `${tagText[0].toUpperCase()}${tagText.substring(1)}`;
            }
            // The tooltip renders as HTML, so its shortcut letter is escaped on the way in.
            const tooltipFooter = i18next.t('center-ui.context-menu.label-popup-shortcuts', {
              c: keyChar, interpolation: { escapeValue: true },
            });
            const tooltipImage = `<img class="context-menu-tooltip__img--tag" src="${util.escapeHTML(img)}"/>`;

            button.setAttribute(
              'data-ps-tooltip-html', `${tooltipHeader}<br/>${tooltipImage}<br/> <i>${tooltipFooter}</i>`,
            );
          }).catch(() => {}); // A tag with no example picture simply gets no tooltip.
        }
      });
    }
  }

  /**
   * Set context menu severity tooltips to the correct text/images for the given label type.
   * @param {string} labelType
   */
  #setSeverityTooltips(labelType) {
    const tooltipKey = util.misc.isPositiveLabelType(labelType)
      ? 'quality-example-tooltip'
      : 'severity-example-tooltip';
    for (let sev = 1; sev < 4; sev++) {
      // Add severity tooltips for the current label type if we have images for them.
      util.getImage(util.assetPath(`images/examples/severity/${labelType}_Severity${sev}.png`)).then((img) => {
        const tooltipHeader = i18next.t(`common:${tooltipKey}-${sev}`);
        const tooltipFooter = `<i>${i18next.t('center-ui.context-menu.severity-shortcuts')}</i>`;
        // Image size (and aspect ratio) is set in CSS so it scales with the UI; see svl-context-menu.css.
        const tooltipImage = `<img class="context-menu-tooltip__img--severity" src="${util.escapeHTML(img)}"/>`;
        for (const button of document.querySelectorAll(`.severity-button[data-severity="${sev}"]`)) {
          button.setAttribute('data-ps-tooltip-html', `${tooltipHeader}<br/>${tooltipImage}<br/>${tooltipFooter}`);
        }
      });
    }
  }

  /**
   * Remove severity tooltips from the context menu, preparing to replace them for a new label type.
   */
  #removePrevSeverityTooltips() {
    for (let severity = 1; severity < 4; severity++) {
      for (const button of document.querySelectorAll(`.severity-button[data-severity="${severity}"]`)) {
        button.removeAttribute('data-ps-tooltip-html');
      }
    }
  }

  /**
   * Show the context menu.
   * @param {Label} targetLabel - The label whose context menu should be shown.
   */
  show(targetLabel) {
    this.#setStatus('targetLabel', null);
    for (const radio of this.#severityRadios) radio.checked = false;
    this.#descriptionTextBox.value = '';

    const labelType = targetLabel.getLabelType();
    const labelCoord = targetLabel.getCanvasXY();

    if (labelType !== 'Occlusion') {
      this.#setStatus('targetLabel', targetLabel);

      // Identify the label being edited, the way the collapsed card does. Without this the panel opens on its
      // first field and never says what it belongs to.
      if (this.#headerIcon) this.#headerIcon.src = util.misc.getIconImagePaths(labelType).iconImagePath;
      if (this.#headerType) {
        this.#headerType.textContent = util.misc.labelTypeName(labelType);
      }
      // The tutorial can forbid deleting the label it just had you place.
      this.#menuWindow.classList.toggle('context-menu--no-delete', !this.#labelContainer.canDelete(targetLabel));

      this.#setTags(targetLabel);
      this.#setTagColor(targetLabel);

      // Hide the severity menu for label types that don't have a severity rating.
      this.#severityMenu.classList.toggle('ps-hidden', !util.misc.labelTypeHasSeverity(labelType));
      // Set the menu value if label has its value set.
      const severity = targetLabel.getProperty('severity');
      const description = targetLabel.getProperty('description');
      if (severity) {
        for (const radio of this.#severityRadios) radio.checked = parseInt(radio.value, 10) === severity;
      }

      // Enable rating severity and tagging on tutorial labels if appropriate.
      if (this.#config.isOnboarding) {
        if (!this.isRatingSeverityDisabled()) this.#showRatingSeverityEnabled();
        else this.#showRatingSeverityDisabled();
        if (!this.isTaggingDisabled()) this.#showTaggingEnabled();
        else this.#showTaggingDisabled();
      }

      this.#setStatus('visibility', 'visible');

      if (description) this.#descriptionTextBox.value = description;

      // Don't push event on Occlusion labels; they don't open ContextMenus.
      this.#tracker.push('ContextMenu_Open', null, targetLabel);
    }
    if (util.misc.labelTypeHasSeverity(labelType)) {
      this.updateRadioButtonImages();
      this.#updateRatingText();
      this.#removePrevSeverityTooltips();
      if (labelType !== 'Other') {
        this.#setSeverityTooltips(labelType);
      }
    }

    // Anchor the menu exactly like the hover card it opens from, so clicking a label expands the card into this
    // menu roughly in place instead of moving the panel somewhere else. This runs last because the routine measures
    // the menu, and everything above — which sections are shown, how many tag rows there are — sets its height.
    // targetLabel is only set for label types that get a menu at all (Occlusion doesn't).
    if (this.#getStatus('targetLabel')) {
      this.#pointShareAtLabel(this.#getStatus('targetLabel'));
      util.anchorPanelToLabel(this.#menuWindow, labelCoord, this.#frame.iconRadius);
      this.#menuWindow.style.visibility = 'visible';

      // The target label's icon fades (see Label.render). Not every caller renders after opening the panel, and the
      // ones that do — the hover card's click handler, for one — render before it, so it is announced from here.
      this.#changeListeners.forEach((listener) => listener());
    }
  }

  /**
   * Builds the header's share control. Unlike the hover card's, this one is only ever pointed at a label the menu
   * has actually opened for, so it re-points once per open rather than per frame.
   */
  #initShareWidget() {
    const trigger = /** @type {HTMLButtonElement} */ (document.getElementById('context-menu-share'));
    if (!trigger) return;
    trigger.addEventListener('click', () => {
      // Only the opening click. The same handler runs on the click that dismisses the popover, which is not a share.
      if (this.#shareWidget?.isOpen()) return;
      this.#tracker.push('Click_LabelCardShare', { labelType: this.#getStatus('targetLabel')?.getLabelType() });
    });
    // Same submit-then-share path as the collapsed card: this menu opens the moment a label is placed, which is the
    // most likely moment for it to have no server-side id yet.
    this.#shareWidget = new ShareWidget(trigger, {
      // Like the collapsed card, this panel is anchored to a label icon and can sit anywhere in the pano.
      fitToViewport: true,
      beforeOpen: async () => {
        const label = this.#getStatus('targetLabel');
        await this.#form.ensureLabelSaved(label);
        if (label) this.#pointShareAtLabel(label);
      },
    });
  }

  /**
   * Points the share control at the label this menu is open for, or hides it when that label can never have a
   * public URL (tutorial labels are never submitted).
   * @param {Label} label
   */
  #pointShareAtLabel(label) {
    const shareable = !this.#config.isOnboarding && !label.isDeleted();
    this.#menuWindow.classList.toggle('context-menu--no-share', !shareable);
    if (!this.#shareWidget || !shareable) return;

    const id = label.getProperty('labelId');
    const labelTypeName = util.misc.labelTypeName(label.getLabelType());
    const text = i18next.t('common:share.text', { labelType: labelTypeName });
    this.#shareWidget.setTarget({
      url: Number.isInteger(id) ? `${window.location.origin}/label/${id}` : '',
      title: text,
      text,
    });
  }
}
